import { ArrowUpRight, RotateCcw, Wallet } from "lucide-react";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { offerings, payouts } from "@/mocks/residual";
import { formatDate, formatUsd } from "@/lib/format";
const STATUS_TONE: Record<string, string> = {
  credited: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  withdrawn: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  reinvested: "bg-violet-500/15 text-violet-700 dark:text-violet-300"
};
const STATUS_ICON = {
  credited: Wallet,
  withdrawn: ArrowUpRight,
  reinvested: RotateCcw
};
export function PayoutsPage() {
  const grouped = useMemo(() => {
    const by: Record<string, typeof payouts> = {};
    for (const p of payouts) {
      (by[p.periodLabel] = by[p.periodLabel] || []).push(p);
    }
    return Object.entries(by);
  }, []);
  const total = payouts.reduce((s, p_0) => s + p_0.amountUsd, 0);
  return <div className="space-y-5">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Income</h1>
				<p className="text-sm text-muted-foreground">
					Pro-rata payouts from each of your held assets.
				</p>
			</div>

			<Card>
				<CardContent className="pt-6">
					<div className="text-xs uppercase tracking-wide text-muted-foreground">
						Lifetime income
					</div>
					<div className="text-3xl font-semibold tabular-nums">
						{formatUsd(total)}
					</div>
					<div className="text-xs text-muted-foreground">
						Across {payouts.length} distribution
						{payouts.length === 1 ? "" : "s"}
					</div>
				</CardContent>
			</Card>

			{grouped.length === 0 ? <div className="rounded-2xl border border-dashed p-10 text-center">
					<p className="text-sm font-medium">No payouts yet</p>
					<p className="mt-1 text-xs text-muted-foreground">
						Once your held offerings start distributing income, you'll see it
						here.
					</p>
				</div> : grouped.map(([period, items]) => <div key={period} className="space-y-2">
						<div className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
							{period}
						</div>
						<Card>
							<CardContent className="divide-y px-0 py-0">
								{items.map((p_1, i) => {
            const o = offerings.find(x => x.id === p_1.offeringId);
            const Icon = STATUS_ICON[p_1.status];
            return <div key={p_1.id} className="flex items-center gap-3 px-5 py-3">
											<div className={`grid size-9 shrink-0 place-items-center rounded-lg ${STATUS_TONE[p_1.status]}`}>
												<Icon className="size-4" />
											</div>
											<div className="flex-1">
												<div className="text-sm font-medium">
													{o?.title ?? "Unknown offering"}
												</div>
												<div className="text-xs text-muted-foreground">
													{formatDate(p_1.receivedAt)}
												</div>
											</div>
											<div className="text-right">
												<div className="text-sm font-semibold tabular-nums">
													+{formatUsd(p_1.amountUsd)}
												</div>
												<Badge variant="outline" className="mt-0.5 rounded-full px-2 py-0 text-[10px] font-normal capitalize">
													{p_1.status}
												</Badge>
											</div>
										</div>;
          })}
							</CardContent>
						</Card>
					</div>)}
		</div>;
}